import type {
  FinancialDailyReportFinancialDailyFiltersProps,
  FinancialDailyReportListParams,
  FinancialDailyReportSortKey,
  FinancialDailyReportVariant,
  FinancialDailyReportVisibleFinancialDailyRow,
} from '../types/pages'
/**
 * Financial Daily reports — shared React page for standard and defaults-paid variants.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Download } from 'lucide-react'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { PageShell } from '@/components/layout/PageShell'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { ListFiltersButton } from '@/components/ui/ListFiltersButton'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { formatAmount } from '@/lib/utils/formatters'
import { showApiError } from '@/lib/utils/notifications'
import {
  ReportDataTable,
  type ReportDataTableColumn,
} from '../components/ReportDataTable'
import { ReportDateRangePicker } from '../components/ReportDateRangePicker'
import inviteStyles from '@/features/invitee/styles/invitesParity.module.css'
import { getFinancialDailyDefaultsReportList, getFinancialDailyReportList } from '../services/reportsService'
import type { FinancialDailyReportListResponse, FinancialDailyReportRow } from '../types/api'
import {
  buildReportExportUrl,
  appendIndexedArrayParams,
} from '../utils/reportExportUrls'
import {
  buildFinancialDailySessionFromParams,
  clearFinancialDailySession,
  FINANCIAL_DAILY_DEFAULTS_SESSION_KEY,
  FINANCIAL_DAILY_SESSION_KEY,
  readFinancialDailySession,
  sessionFilterToFinancialDailyPatch,
  writeFinancialDailySession,
} from '../utils/financialDailySessionFilter'
import type { FinancialDailySessionKey } from '../types/utils'
import { PAYMENT_SOURCE_OPTIONS } from '../constants/pages/financialDailyReportPage'
import { LABEL_CLASS, REPORT_EXPORT_BUTTON_CLASS } from '../constants/configuredReportStyles'

function currentMonthDefaults(): Pick<FinancialDailyReportListParams, 'fromDt' | 'toDt'> {
  const now = new Date()
  return {
    fromDt: formatDateForAPI(new Date(now.getFullYear(), now.getMonth(), 1)),
    toDt: formatDateForAPI(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  }
}

function configForFinancialDailyReportVariant(variant: FinancialDailyReportVariant) {
  if (variant === 'defaults') {
    return {
      title: 'Financial Daily Report - Defaults Paid',
      testIdPrefix: 'financial-daily-defaults',
      sessionKey: FINANCIAL_DAILY_DEFAULTS_SESSION_KEY,
      exportPath: '/api/export/daily_payment_default_extended',
      load: getFinancialDailyDefaultsReportList,
    }
  }
  return {
    title: 'Financial Daily Report',
    testIdPrefix: 'financial-daily',
    sessionKey: FINANCIAL_DAILY_SESSION_KEY,
    exportPath: '/api/export/daily_payment_extended',
    load: getFinancialDailyReportList,
  }
}

function defaultParams(): FinancialDailyReportListParams {
  return {
    page: 1,
    perPage: 50,
    sortBy: 'desc',
    orderBy: 'created_at',
    payment_source: [],
    ...currentMonthDefaults(),
  }
}

function initialParams(sessionKey: FinancialDailySessionKey): FinancialDailyReportListParams {
  const session = readFinancialDailySession(sessionKey)
  if (!session) return defaultParams()
  return { ...defaultParams(), ...sessionFilterToFinancialDailyPatch(session) }
}

function buildFinancialDailyQueryParams(
  p: FinancialDailyReportListParams,
  indexedArrays = false,
): URLSearchParams {
  const qs = new URLSearchParams()
  qs.set('page', String(p.page))
  qs.set('perPage', String(p.perPage))
  qs.set('sortBy', p.sortBy)
  qs.set('orderBy', p.orderBy)
  if (p.fromDt) qs.set('fromDt', p.fromDt)
  if (p.toDt) qs.set('toDt', p.toDt)
  const paymentSource = p.payment_source.filter(Boolean)
  if (indexedArrays) {
    appendIndexedArrayParams(qs, 'payment_source', paymentSource)
  } else {
    paymentSource.forEach((source) => qs.append('payment_source[]', source))
  }
  return qs
}

function toNumber(value: string | number | undefined): number {
  if (value == null || value === '') return 0
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function money(value: string | number | undefined): string {
  return formatAmount(toNumber(value))
}

function defaultNetDue(row: FinancialDailyReportRow): string {
  return formatAmount(toNumber(row.DefaultsPaid) - toNumber(row.RefundFees))
}

function isWeekRow(row: FinancialDailyReportRow): boolean {
  return Boolean(row.PaymentSource?.includes('Week'))
}

function countActiveFilters(p: FinancialDailyReportListParams): number {
  const defaults = defaultParams()
  let count = 0
  if (
    JSON.stringify(p.payment_source) !== JSON.stringify(defaults.payment_source)
  )
    count++
  if (p.fromDt !== defaults.fromDt || p.toDt !== defaults.toDt) count++
  return count
}


function FinancialDailyFilters({
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
  variant,
}: FinancialDailyReportFinancialDailyFiltersProps) {
  const [draft, setDraft] = useState<FinancialDailyReportListParams>(filters)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: re-sync the filter draft to applied filters each time the drawer opens
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      onDone={() => {
        onApply(draft)
        onClose()
      }}
      testIdPrefix={
        variant === 'defaults' ? 'financial-daily-defaults' : 'financial-daily'
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className={LABEL_CLASS}>Payment Source</label>
          <SearchableSelect
            options={PAYMENT_SOURCE_OPTIONS[variant]}
            value={draft.payment_source}
            onChange={(value) =>
              setDraft((prev) => ({
                ...prev,
                payment_source: Array.isArray(value) ? value : [],
              }))
            }
            placeholder="All"
            multiple
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Date Range</label>
          <ReportDateRangePicker
            startDate={draft.fromDt}
            endDate={draft.toDt}
            onChange={(start, end) =>
              setDraft((prev) => ({
                ...prev,
                fromDt: start ? formatDateForAPI(start) : '',
                toDt: end ? formatDateForAPI(end) : '',
              }))
            }
          />
        </div>
      </div>
    </ListFilterDrawer>
  )
}

function FinancialDailyReport({ variant }: { variant: FinancialDailyReportVariant }) {
  const config = useMemo(() => configForFinancialDailyReportVariant(variant), [variant])
  const [params, setParams] = useState<FinancialDailyReportListParams>(() =>
    initialParams(config.sessionKey),
  )
  const [rows, setRows] = useState<FinancialDailyReportRow[]>([])
  const [response, setResponse] =
    useState<FinancialDailyReportListResponse | null>(null)
  const [pageOptions, setPageOptions] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [expandedDate, setExpandedDate] = useState('')

  const load = useCallback(
    async (next: FinancialDailyReportListParams) => {
      setLoading(true)
      try {
        const res = await config.load(buildFinancialDailyQueryParams(next))
        setRows(res.data ?? [])
        setResponse(res)
        setPageOptions({
          current: res.current ?? 1,
          last: Math.max(1, res.last ?? 1),
          total: res.total ?? 0,
        })
        writeFinancialDailySession(
          config.sessionKey,
          buildFinancialDailySessionFromParams(next),
        )
      } catch (err) {
        showApiError(err)
      } finally {
        setLoading(false)
      }
    },
    [config],
  )

  useEffect(() => {
    void load(params)
  }, [load, params])

  const visibleRows = useMemo<FinancialDailyReportVisibleFinancialDailyRow[]>(() => {
    return rows.flatMap((row, index) => {
      const rowId = `${row.PaymentDate ?? row.PaymentSource ?? 'row'}-${index}`
      const parent: FinancialDailyReportVisibleFinancialDailyRow = {
        ...row,
        _rowId: rowId,
        _depth: 0,
      }
      if (
        !row.PaymentDate ||
        expandedDate !== row.PaymentDate ||
        !row.details?.length
      ) {
        return [parent]
      }
      return [
        parent,
        ...row.details.map((child, childIndex) => ({
          ...child,
          _rowId: `${rowId}-detail-${child.PaymentSource ?? childIndex}`,
          _depth: 1 as const,
        })),
      ]
    })
  }, [expandedDate, rows])

  const exportUrl = useMemo(
    () =>
      buildReportExportUrl(
        config.exportPath,
        buildFinancialDailyQueryParams(params, true),
      ),
    [config.exportPath, params],
  )

  const sort = (orderBy: FinancialDailyReportSortKey) => {
    setParams((prev) => ({
      ...prev,
      orderBy,
      sortBy:
        prev.orderBy === orderBy && prev.sortBy === 'desc' ? 'asc' : 'desc',
      page: 1,
    }))
  }

  const resetFilters = () => {
    clearFinancialDailySession(config.sessionKey)
    setExpandedDate('')
    setParams(defaultParams())
  }

  const firstColumn = useMemo<
    ReportDataTableColumn<FinancialDailyReportVisibleFinancialDailyRow, FinancialDailyReportSortKey>
  >(
    () => ({
      id: 'payment-source',
      label: 'Payment Source',
      render: (row) => {
        if (row._depth === 1) {
          return (
            <span className="pl-6 text-slate-700">
              {row.PaymentSource || '—'}
            </span>
          )
        }
        if (row.details?.length && row.PaymentDate && !isWeekRow(row)) {
          const expanded = expandedDate === row.PaymentDate
          return (
            <button
              type="button"
              onClick={() =>
                setExpandedDate(expanded ? '' : (row.PaymentDate ?? ''))
              }
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-base font-semibold text-slate-600 hover:bg-slate-100"
              aria-label={expanded ? 'Collapse row' : 'Expand row'}
            >
              {expanded ? '-' : '+'}
            </button>
          )
        }
        return row.PaymentSource || '—'
      },
    }),
    [expandedDate],
  )

  const dateColumn = useMemo<
    ReportDataTableColumn<FinancialDailyReportVisibleFinancialDailyRow, FinancialDailyReportSortKey>
  >(
    () => ({
      id: 'date',
      label: 'Date',
      sortKey: 'PaymentDate',
      render: (row) =>
        isWeekRow(row) ? row.PaymentSource : row.PaymentDate || '—',
    }),
    [],
  )

  const standardColumns = useMemo<
    ReportDataTableColumn<FinancialDailyReportVisibleFinancialDailyRow, FinancialDailyReportSortKey>[]
  >(
    () => [
      firstColumn,
      dateColumn,
      {
        id: 'app-fee',
        label: 'App Fee',
        sortKey: 'AppFeeReceived',
        render: (r) => money(r.AppFeeReceived),
      },
      {
        id: 'access-fee',
        label: 'Access Fee',
        sortKey: 'ContractReceived',
        render: (r) => money(r.ContractReceived),
      },
      {
        id: 'contract-invoice',
        label: 'Contract Invoice',
        sortKey: 'ContractInvoiceReceived',
        render: (r) => money(r.ContractInvoiceReceived),
      },
      {
        id: 'surcharge',
        label: 'Surcharge',
        sortKey: 'SurchargeReceived',
        render: (r) => money(r.SurchargeReceived),
      },
      {
        id: 'received',
        label: 'Received',
        sortKey: 'AmountRecieved',
        render: (r) => money(r.AmountRecieved),
      },
      {
        id: 'ar-count',
        label: 'AR Count',
        sortKey: 'AmountRecievedcount',
        render: (r) => r.AmountRecievedcount ?? '—',
      },
      {
        id: 'app-refunds',
        label: 'App Fee Refunds',
        sortKey: 'AppFeeRefunds',
        render: (r) => money(r.AppFeeRefunds),
      },
      {
        id: 'full-refunds',
        label: 'Full Refunds',
        sortKey: 'ContractFullRefunds',
        render: (r) => money(r.ContractFullRefunds),
      },
      {
        id: 'partial-refunds',
        label: 'Partial Refunds',
        sortKey: 'ContractPartialRefunds',
        render: (r) => money(r.ContractPartialRefunds),
      },
      {
        id: 'refund-fees',
        label: 'Refund Fees',
        sortKey: 'RefundFees',
        render: (r) => money(r.RefundFees),
      },
      {
        id: 'paid',
        label: 'Paid',
        sortKey: 'Refunds',
        render: (r) => money(r.Refunds),
      },
      {
        id: 'paid-count',
        label: 'Paid Count',
        sortKey: 'Refundcount',
        render: (r) => r.Refundcount ?? '—',
      },
    ],
    [dateColumn, firstColumn],
  )

  const defaultsColumns = useMemo<
    ReportDataTableColumn<FinancialDailyReportVisibleFinancialDailyRow, FinancialDailyReportSortKey>[]
  >(
    () => [
      firstColumn,
      dateColumn,
      {
        id: 'default-net-due',
        label: 'Default Net Due',
        sortKey: 'ContractFullRefunds',
        render: defaultNetDue,
      },
      {
        id: 'service-fees',
        label: 'Service Fees',
        sortKey: 'RefundFees',
        render: (r) => money(r.RefundFees),
      },
      {
        id: 'default-paid',
        label: 'Default Paid',
        sortKey: 'DefaultsPaid',
        render: (r) => money(r.DefaultsPaid),
      },
      {
        id: 'paid-count',
        label: 'Paid Count',
        sortKey: 'Refundcount',
        render: (r) => r.Refundcount ?? '—',
      },
    ],
    [dateColumn, firstColumn],
  )

  const columns = variant === 'defaults' ? defaultsColumns : standardColumns
  const footer = response?.footer?.[0]

  const footerRow =
    footer && rows.length ? (
      variant === 'defaults' ? (
        <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
          <td>Total</td>
          <td />
          <td>{defaultNetDue(footer)}</td>
          <td>{money(footer.RefundFees)}</td>
          <td>{money(footer.DefaultsPaid)}</td>
          <td>{footer.Refundcount ?? '—'}</td>
        </tr>
      ) : (
        <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
          <td>Total</td>
          <td />
          <td>{money(footer.AppFeeReceived)}</td>
          <td>{money(footer.ContractReceived)}</td>
          <td>{money(footer.ContractInvoiceReceived)}</td>
          <td>{money(footer.SurchargeReceived)}</td>
          <td>{money(footer.AmountRecieved)}</td>
          <td>{footer.AmountRecievedcount ?? '—'}</td>
          <td>{money(footer.AppFeeRefunds)}</td>
          <td>{money(footer.ContractFullRefunds)}</td>
          <td>{money(footer.ContractPartialRefunds)}</td>
          <td>{money(footer.RefundFees)}</td>
          <td>{money(footer.Refunds)}</td>
          <td>{footer.Refundcount ?? '—'}</td>
        </tr>
      )
    ) : null

  return (
    <div data-testid={`${config.testIdPrefix}-page`}>
      <PageShell
        title={config.title}
        pageHeaderTestIdRoot={`${config.testIdPrefix}-page-header`}
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-700">Filters</p>
              <p className="mt-1 text-sm text-slate-500">
                Date: {params.fromDt || 'Any'} to {params.toDt || 'Any'}
                {params.payment_source.length
                  ? ` • Sources: ${params.payment_source.join(', ')}`
                  : ' • Sources: All'}
              </p>
              {response?.graph?.length ? (
                <div className="mt-3 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                  {response.graph.map((graphRow, index) => {
                    const total =
                      variant === 'defaults'
                        ? toNumber(response.footer?.[0]?.Refunds)
                        : toNumber(response.footer?.[0]?.AmountRecieved)
                    const amount =
                      variant === 'defaults'
                        ? toNumber(graphRow.Refunds)
                        : toNumber(graphRow.AmountRecieved)
                    const percent = total
                      ? ((amount / total) * 100).toFixed(2)
                      : '0.00'
                    return (
                      <p
                        key={`${graphRow.payment_source ?? 'source'}-${index}`}
                      >
                        {graphRow.payment_source ?? 'Unknown'}:{' '}
                        {formatAmount(amount)} ({percent}%)
                      </p>
                    )
                  })}
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              <ListFiltersButton
                onClick={() => setFiltersOpen(true)}
                activeFilterCount={countActiveFilters(params)}
                testIdPrefix={config.testIdPrefix}
              />
              {exportUrl && rows.length ? (
                <a
                  href={exportUrl}
                  className={REPORT_EXPORT_BUTTON_CLASS}
                >
                  <Download className="h-[18px] w-[18px]" aria-hidden />
                  Export
                </a>
              ) : null}
            </div>
          </div>
        </div>

        <FinancialDailyFilters
          filters={params}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) => {
            setExpandedDate('')
            setParams({ ...next, page: 1 })
          }}
          onReset={resetFilters}
          variant={variant}
        />

        <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
          <ListPaginationBar
            placement="header"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix={config.testIdPrefix}
            summaryLayout="angular"
          />

          <ReportDataTable
            rows={visibleRows}
            columns={columns}
            loading={loading}
            orderBy={params.orderBy}
            sortBy={params.sortBy}
            testIdPrefix={config.testIdPrefix}
            rowKey={(row) => row._rowId}
            onSort={sort}
            footer={footerRow}
            wrapperClassName={inviteStyles.financialDailyTableWrapper}
            tableClassName={inviteStyles.financialDailyListingTable}
            rowClassName={(row) =>
              row._depth === 1
                ? 'bg-sky-100 font-medium'
                : isWeekRow(row)
                  ? 'bg-slate-50/70 font-medium'
                  : undefined
            }
          />

          <ListPaginationBar
            placement="footer"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix={config.testIdPrefix}
            summaryLayout="angular"
          />
        </div>
      </PageShell>
    </div>
  )
}

export function FinancialDailyReportPage() {
  return <FinancialDailyReport variant="standard" />
}

export function FinancialDailyDefaultsReportPage() {
  return <FinancialDailyReport variant="defaults" />
}
